import { useEffect, useMemo } from "react";
import { DateLocalizer, Navigate } from "react-big-calendar";
// @ts-expect-error This is necessary probably due to react-big-calendar not having a good TS implementation :(
import TimeGrid from "react-big-calendar/lib/TimeGrid";
import { useSelector } from "react-redux";
import { getStandardWorkingHours, selectWorkingHoursLoading, selectStandardWorkingHours } from "./WorkWeekViewSlice";
import { WorkingHoursModel } from "services/api/cplapi/CPLAPI";
import dayjs, { setDateTime } from "global/dayjs";
import { useThunkDispatch } from "setup/redux/thunkDispatch";
import { ActivityIndicator } from "cpl-react-common/components/ActivityIndicators/ActivityIndicator";

export default function WorkWeekView({ date, localizer, ...props }: { date: Date; localizer: DateLocalizer }) {
  const thunkDispatch = useThunkDispatch();
  useEffect(() => {
    thunkDispatch(getStandardWorkingHours());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const standardWorkingHours = useSelector(selectStandardWorkingHours);
  const openTimes = standardWorkingHours
    .filter((i) => {
      return i.isOpen;
    })
    .map((i) => setDateTime(dayjs().startOf("day"), i.openTime));
  const closeTimes = standardWorkingHours
    .filter((i) => {
      return i.isOpen;
    })
    .map((i) => setDateTime(dayjs().startOf("day"), i.closeTime));

  const isLoading = useSelector(selectWorkingHoursLoading);

  // Falls back to now when no day is open
  const min = (dayjs.min(openTimes) ?? dayjs()).toDate();
  const max = (dayjs.max(closeTimes) ?? dayjs()).toDate();

  const currRange = useMemo(
    () => WorkWeekView.range(date, standardWorkingHours, { localizer }),
    [date, standardWorkingHours, localizer]
  );

  return (
    <>
      {isLoading ? (
        <ActivityIndicator isBusy={isLoading} />
      ) : (
        <TimeGrid
          localizer={localizer}
          max={max}
          min={min}
          range={currRange}
          scrollToTime={dayjs().startOf("day").toDate()}
          {...props}
        />
      )}
    </>
  );
}

WorkWeekView.range = (
  date: Date,
  standardWorkingHours: WorkingHoursModel[],
  { localizer }: { localizer: DateLocalizer }
) => {
  const startOfWeek = dayjs(date).startOf("week").toDate();
  const endOfWeek = dayjs(date).endOf("week").toDate();

  let current = startOfWeek;
  const range = new Array<Date>();

  if (standardWorkingHours.length === 0) return range;

  while (localizer.lte(current, endOfWeek, "day")) {
    //dayjs weekday is zero based but we store one based
    const dayOfWeek = dayjs(current).weekday() + 1;
    if (standardWorkingHours.find((i) => i.dayOfWeek === dayOfWeek)?.isOpen) {
      range.push(current);
    }
    current = localizer.add(current, 1, "day");
  }

  return range;
};

WorkWeekView.navigate = (date: Date, action: string, { localizer }: { localizer: DateLocalizer }) => {
  switch (action) {
    case Navigate.PREVIOUS:
      return localizer.add(date, -1, "week");
    case Navigate.NEXT:
      return localizer.add(date, 1, "week");
    default:
      return date;
  }
};

WorkWeekView.title = (date: Date) => {
  const startOfWeek = dayjs(date).startOf("week").toDate();
  const endOfWeek = dayjs(date).endOf("week").toDate();
  const sameMonth = startOfWeek.getMonth() === endOfWeek.getMonth();
  return (
    startOfWeek.toLocaleString("en-EN", { month: "long" }) +
    " " +
    startOfWeek.getDate().toString().padStart(2, "0") +
    " - " +
    (sameMonth ? "" : endOfWeek.toLocaleString("en-EN", { month: "long" })) +
    " " +
    endOfWeek.getDate().toString().padStart(2, "0")
  );
};
